import {
  Avatar,
  Button,
  Card,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Form,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
} from "@/components/ui";
import { IEdoCoursePost } from "@/shared/model/edo-course-post.model";
import { IEdoFile } from "@/shared/model/edo-file.model";
import { IEdoWallPost } from "@/shared/model/edo-wall-post.model";
import { IUser } from "@/shared/model/user.model";
import { cn } from "@/shared/util/cn";
import { mapIdList } from "@/shared/util/entity-utils";
import { getFileAvatar } from "@/shared/util/file-avatar-utils";
import { Archive, Paperclip, Upload } from "lucide-react";
import React, { createRef, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { z } from "zod";
import { SelectFilesDialog } from "../SelectFiles/SelectFilesDialog";

function CreatePostDescriptionField({
  userFirstName,
}: {
  userFirstName?: string;
}) {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  return (
    <Controller
      name="description"
      control={control}
      render={({ field }) => (
        <div className="w-full">
          <textarea
            {...field}
            rows={4}
            className={cn(
              "flex w-full rounded-2xl border-2 border-[var(--color-mockup-border)] bg-surface px-4 py-3 text-sm font-editorial text-[var(--color-mockup-text)] placeholder:text-[var(--color-mockup-text-light)] focus:outline-none focus:border-primary focus:shadow-[0_4px_12px_rgba(18,181,170,0.15)] transition-all duration-200 resize-none",
              errors.description && "border-[var(--color-mockup-coral)] focus:border-[var(--color-mockup-coral)] focus:shadow-[0_4px_12px_rgba(255,107,91,0.15)]"
            )}
            placeholder={"Cosa c'è di nuovo?"}
          />
          {errors.description?.message && (
            <p className="mt-1 text-sm text-[var(--color-mockup-coral)]">
              {String(errors.description.message)}
            </p>
          )}
        </div>
      )}
    />
  );
}

interface ICreatePostProps {
  user: IUser;
  defaultValuePost: IEdoWallPost | IEdoCoursePost;
  createPost: (form: IEdoWallPost | IEdoCoursePost) => void;
  uploadFiles: (files: File[]) => Promise<IEdoFile[]>;
}

const CreatePost = (props: ICreatePostProps) => {
  const [files, setFiles] = useState<File[]>([]);
  const [expanded, setExpanded] = useState<boolean>(false);
  const [updating, setUpdating] = useState<boolean>(false);
  const [form, setForm] = useState<IEdoWallPost | IEdoCoursePost>({
    ...props.defaultValuePost,
  });
  const [selectFilesDialog, setSelectFilesDialog] = useState<{
    open: boolean;
  }>({ open: false });

  const fileInput = createRef<HTMLInputElement>();

  const { defaultValuePost, createPost, uploadFiles, user } = props;

  const handleSelectedFiles = (selectedFiles: IEdoFile[]) => {
    setForm((prevState) => ({ ...prevState, files: selectedFiles }));
  };

  const openSelectFilesDialog = () => setSelectFilesDialog({ open: true });

  const closeSelectFilesDialog = () => setSelectFilesDialog({ open: false });

  const handleExpandClick = () => setExpanded(!expanded);

  const handleCancel = () => {
    setExpanded(false);
    setFiles([]);
    setForm({ ...defaultValuePost });
  };

  const removeFile = (index: number) => {
    setFiles((prevState) => {
      const next = Array.from(prevState);
      next.splice(index, 1);
      return next;
    });
  };

  const removeRepositoryFile = (index: number) => {
    setForm((prevState) => {
      const selectedFiles = Array.from(prevState.files || []);
      selectedFiles.splice(index, 1);
      return { ...prevState, files: selectedFiles };
    });
  };

  const saveEntity = async (description: string) => {
    setUpdating(true);
    const payload = { ...form, description };
    if (files.length) {
      const uploadedFiles = await uploadFiles(files);
      createPost({
        ...payload,
        files: [
          ...(form.files || []),
          ...mapIdList(uploadedFiles),
        ],
      });
    } else {
      createPost(payload);
    }
    setUpdating(false);
    handleCancel();
  };

  const selectFiles = () => fileInput.current?.click();

  const handleFiles = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFiles((prev) => [
      ...prev,
      ...Array.from(event.target.files || []),
    ]);
  };

  return (
    <Card
      className={cn(
        "flex-1 overflow-hidden rounded-3xl border-2 transition-colors duration-200 shadow-card p-0",
        expanded ? "border-primary" : "border-[var(--color-mockup-border)] hover:border-primary/40",
      )}
    >
      <div
        role="button"
        tabIndex={0}
        onClick={handleExpandClick}
        onKeyDown={(e) => e.key === "Enter" && handleExpandClick()}
        className="flex cursor-pointer items-center gap-4 p-5"
      >
        <Avatar
          fallback={`${user.firstName?.[0] ?? ""}${user.lastName?.[0] ?? ""}` || "U"}
        />
        <span className="min-w-0 flex-1 text-sm font-editorial italic text-[var(--color-mockup-text-light)]">
          {"Cosa vuoi condividere, " + user.firstName + "?"}
        </span>
        <div
          className="text-xs font-bold px-4 py-2 rounded-full text-white flex-shrink-0"
          style={{
            background: 'var(--color-primary)',
            boxShadow: '0 4px 12px color-mix(in srgb, var(--color-primary) 27%, transparent)',
          }}
        >
          + Pubblica
        </div>
      </div>
      {expanded && (
        <div className="border-t border-[var(--color-mockup-border)]">
          <Form
            schema={z.object({
              description: z.string().min(1, "Inserisci un messaggio."),
            })}
            defaultValues={{ description: form.description || "" }}
            onSubmit={(data) => saveEntity(data.description)}
            className="flex flex-1 flex-col"
          >
            <div className="px-5 pb-4 pt-4">
              <CreatePostDescriptionField userFirstName={user.firstName} />
            </div>
            <div className="space-y-4 px-5 pb-5">
              {files?.length ? (
                <div>
                  <p className="mb-2 text-sm font-semibold text-[var(--color-mockup-text)]">
                    File da caricare
                  </p>
                  <List className="flex flex-col divide-y divide-[var(--color-mockup-border)]">
                    {files.map((file, index) => (
                      <ListItem
                        key={index}
                        className="flex items-center gap-3 rounded-2xl py-2 hover:bg-primary/5"
                      >
                        <div className="mr-3 shrink-0">
                          {getFileAvatar(file.type)}
                        </div>
                        <ListItemText primary={file.name} />
                        <ListItemSecondaryAction>
                          <IconButton
                            onClick={() => removeFile(index)}
                            aria-label="Rimuovi file"
                          >
                            <span className="text-lg leading-none">×</span>
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </div>
              ) : null}
              {form.files?.length ? (
                <div>
                  <p className="mb-2 text-sm font-semibold text-[var(--color-mockup-text)]">
                    File dall&apos;archivio
                  </p>
                  <List className="flex flex-col divide-y divide-[var(--color-mockup-border)]">
                    {form.files.map((file, index) => (
                      <ListItem
                        key={file.id}
                        className="flex items-center gap-3 rounded-2xl py-2 hover:bg-primary/5"
                      >
                        <div className="mr-3 shrink-0">
                          {getFileAvatar(file.fileType || "")}
                        </div>
                        <ListItemText primary={file.originalFileName} />
                        <ListItemSecondaryAction>
                          <IconButton
                            onClick={() => removeRepositoryFile(index)}
                            aria-label="Rimuovi file"
                          >
                            <span className="text-lg leading-none">×</span>
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </div>
              ) : null}
            </div>
            <div className="flex flex-wrap items-center justify-between gap-2 border-t border-[var(--color-mockup-border)] px-5 py-4">
              <div className="flex gap-2">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      className="flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full"
                      style={{
                        background: 'var(--color-mockup-coral-light)',
                        color: 'var(--color-mockup-coral)',
                        border: '1px solid color-mix(in srgb, var(--color-mockup-coral) 20%, transparent)',
                      }}
                    >
                      <Paperclip className="h-3.5 w-3.5" />
                      Allegato
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    <DropdownMenuItem onSelect={selectFiles}>
                      <Upload className="mr-2 h-4 w-4" />
                      Carica file
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={openSelectFilesDialog}>
                      <Archive className="mr-2 h-4 w-4" />
                      Seleziona dall&apos;archivio
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <input
                  type="file"
                  ref={fileInput}
                  name="files"
                  multiple
                  hidden
                  onChange={handleFiles}
                />
                <button
                  type="button"
                  onClick={handleCancel}
                  className="flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full border border-[var(--color-mockup-border)] text-[var(--color-mockup-text-light)] hover:bg-[var(--color-mockup-border)]/30"
                >
                  Annulla
                </button>
              </div>
              <Button type="submit" isLoading={updating}>
                Pubblica
              </Button>
            </div>
          </Form>
        </div>
      )}
      <SelectFilesDialog
        open={selectFilesDialog.open}
        handleClose={closeSelectFilesDialog}
        selectedFiles={form.files || []}
        setSelectedFiles={handleSelectedFiles}
      />
    </Card>
  );
};

export default CreatePost;
